import { CellEditingStoppedEvent, RowNode } from "ag-grid-community";
import { Prop, Vue } from "vue-property-decorator";
import store from "../../stores/store";
import { Attribute, AttributeValues } from "@/interfaces/attributes/attribute";
import { getLocaleText } from "./grid-locale";
import { parse } from "csv-parse/browser/esm";
import moment from "moment";
import GridImportTextEditorComponent from "../../components/_shared/grid-components/grid-cell-editors/grid-import-text-editor-component.vue";
import GridImportSelectEditorComponent from "../../components/_shared/grid-components/grid-cell-editors/grid-import-select-editor-component.vue";
import router from "../../router/router";
import { AttributeHelper } from "../attribute-helper";
import GridImportBooleanEditorComponent from "../../components/_shared/grid-components/grid-cell-editors/grid-import-boolean-editor-component.vue";
import GridBoolComponent from "../../components/_shared/grid-components/grid-cells/grid-bool-component.vue";
import { MessageType } from "../../components/_shared/template/app-snackbar.vue";
import { i18n } from "@/main";
import { formValidation } from "../form-validation-helper";
import GridImportMultiSelectEditorComponent from "../../components/_shared/grid-components/grid-cell-editors/grid-import-multiselect-editor-component.vue";
import GridLineActionsComponent from "../../components/_shared/grid-components/grid-cells/grid-line-actions-component.vue";

import "../../assets/css/ag-grid/import-grid.scss";
import "../../assets/css/ag-grid/ag-theme-dartess-import.scss";
import { csvImportApiProvider } from "@/providers/csv-import-api-provider";
import { userApiProvider } from "@/providers/user-api-provider";

export interface ImportGridData {
  value: string;
  validity: CellValidity;
  title: string;
}

export function formatImportGridDataToData(data: any): object {
  const result: any = {};
  for (const key in data) {
    const value: ImportGridData = data[key];
    result[key] = value.value;
  }
  return result;
}

enum CellValidity {
  "valid" = 1,
  "invalid" = 0,
  "unverified" = 2,
  "required" = 3,
}

function preventUnload(event: BeforeUnloadEvent) {
  event.preventDefault();
  event.returnValue = "";
}

export abstract class ImportGrid extends Vue {
  @Prop() attributeGroup: string;

  csv: string;
  attributes: Array<Attribute> = [];
  templateValidator: Array<string>;

  invalidRow: Map<string, RowNode> = new Map<string, RowNode>();

  attributeHelper = new AttributeHelper();

  gridContext = {
    componentParent: this
  }

  defaultColDef: any = {
    sortable: false,
    filter: false,
    resizable: true,
    minWidth: 110,
    width: 110,
    lockPosition: true,
    editable: true,
    valueParser: (params: any) => {
      return { value: params.newValue, validity: CellValidity.unverified };
    },
    tooltipValueGetter: (params) => {
      if (params.value.validity !== CellValidity.valid && params.value.title) {
        return `${params.value.title}`
      }
    },
    cellClass: (params: any) => {
      const cellClass: any = ["cell-align-center"];
      switch (params.value.validity) {
        case CellValidity.valid:
          cellClass.push("cell-valid");
          break;
        case CellValidity.unverified:
          cellClass.push("cell-unverified");
          break;
        case CellValidity.invalid:
          cellClass.push("cell-invalid");
          break;
        case CellValidity.required:
          cellClass.push("cell-required");
          break;
      }
      return cellClass;
    },
  };

  gridOptions: any = {
    rowHeight: 30,
    localeText: getLocaleText(),
    suppressCellSelection: true,
    suppressMenuHide: true,
    suppressDragLeaveHidesColumns: true,
    singleClickEdit: true,
    suppressFieldDotNotation: true,
    stopEditingWhenCellsLoseFocus: true
  };

  frameworkComponents: object = {
    GridImportTextEditorComponent: GridImportTextEditorComponent,
    GridImportSelectEditorComponent: GridImportSelectEditorComponent,
    GridImportBooleanEditorComponent: GridImportBooleanEditorComponent,
    GridImportMultiSelectEditorComponent: GridImportMultiSelectEditorComponent,
    GridBoolComponent: GridBoolComponent,
    GridLineActions: GridLineActionsComponent
  };

  rowData: [] = [];

  async beforeMount() {
    // Init page data.
    this.$route.meta.title = this.$t("import_csv.title");
    if (!this.$route.params.csv || !this.$route.params.template) {
      await router.push(this.previousRoute);
    } else {
      this.csv = this.$route.params.csv;
      // Get the CSV validator from the API.
      this.templateValidator = await csvImportApiProvider.getTemplate(
        this.$route.params.template,
        this.$route.params.type,
        false
      );
    }
    // Parse the CSV.
    // =======================================
    // await parse
    parse(
      this.csv,
      {
        comment: "#",
        delimiter: ";",
      },
      async (error, records: any) => {
        if (records) {
          // We generate the attribute list from the first csv line.
          // Le csv ne correspond pas au template : on a été redirigé, inutile de construire la grille.
          if (!(await this.generateAttributeList(records[0]))) return;
          // We delete the csv header.
          records.shift();
          // When the grid is ready, we start a global verification.
          this.gridOptions.onFirstDataRendered = () => {
            this.gridOptions.api.getModel().forEachLeafNode((node: RowNode) => {
              this.dataValidation(node);
            });
            this.$forceUpdate();
          };
          // Add on cell edition stopped callback.
          this.gridOptions.onCellEditingStopped = (event: CellEditingStoppedEvent) => {
            this.dataValidation(event.node);
            this.$forceUpdate();
          };
          // We convert the csv into our specific grid format.
          for (const index in records) {
            records[index] = this.convertToRowData(records[index]);
          }
          // We set the ag-grid data.
          this.rowData = records;
          // Check email not already used in DB
          const usersFound = [];
          for (const record of records) {
            // On cherche une clé contenant "email" (l'id d'attribut n'est pas
            // forcément littéralement "email"), comme dans dataValidation.
            const emailKey = Object.keys(record).find((key) => key.includes("email"));
            if (!emailKey || !record[emailKey]?.value) {
              continue;
            }

            // On ne vérifie l'unicité que si la règle `isUniqueEmail` est
            // activée sur l'attribut correspondant.
            const att = this.attributes.find((a: Attribute) => a.id === emailKey);
            if (att?.rules?.isUniqueEmail !== true) {
              continue;
            }

            const emailFound = await userApiProvider.getUserEmail(record[emailKey].value);
            if (emailFound !== "") {
              usersFound.push(record[emailKey].value);
            }
          }

          if (usersFound.length > 0) {
            this.$eventHub.$emit("email-used", usersFound);
            this.$eventHub.$emit("show-snackbar", {
              type: MessageType.warning,
              mainText: i18n.t("message.emails_already_used").toString(),
              secondText: usersFound.join(" - "),
              // No timeout
              timeout: -1,
            });
          }
          // Generate the column name.
          this.gridOptions.api.setColumnDefs(this.getColumnFromCsvAttribute());
          this.gridOptions.api.sizeColumnsToFit();
        }
      }
    );
  }

  async onValidate(formData = new FormData(), redirect = true) {
    if (this.invalidRow.size > 0) {
      this.$eventHub.$emit("show-snackbar", {
        type: MessageType.error,
        mainText: this.$tc("common.invalid_data", this.invalidRow.size),
      });
    } else {
      const params = {
        columnSeparator: ";",
        processHeaderCallback: (params: any) => params.column.colDef.field,
        processCellCallback: (params: any) => params.value.value,
      };
      const data = this.gridOptions.api.csvCreator.getDataAsCsv(params);
      const blob = new Blob([data], { type: "text/csv" });
      formData.append("file", blob, `${this.$route.params.template}.csv`);

      // Pendant l'envoi, on empêche de quitter la page (navigation interne, retour, fermeture/rechargement de l'onglet).
      const removeRouterGuard = router.beforeEach((to, from, next) => next(false));
      window.addEventListener("beforeunload", preventUnload);
      let response;
      try {
        response = await csvImportApiProvider.uploadFile(
          `/csv/${this.$route.params.template}`,
          formData,
          redirect ? [{ type: MessageType.success, text: this.$t("import_csv.success_snackbar").toString() }] : null
        );
      } finally {
        removeRouterGuard();
        window.removeEventListener("beforeunload", preventUnload);
      }

      if (response) {
        if (redirect) {
          await router.push(this.previousRoute);
        } else {
          return response;
        }
      }
    }
  }

  get previousRoute(): string {
    if (this.$route.name === "import-client-users") {
      return "/client-user/list";
    } else {
      // Toutes les routes d'import sont de la forme `/xxx/import` et ont une liste associée `/xxx/list`.
      const route = this.$route.path.split("/");
      route.splice(-1, 1, "list");
      return route.join("/");
    }
  }

  /// This method generate the array of an ordered column from preferences.
  getColumnFromCsvAttribute(): Array<any> {
    const columnDefs: Array<any> = [
      this.getRowNumberColumn()
    ];

    this.attributes.forEach((att: Attribute) => {
      if (att.isSelect) {
        columnDefs.push(this.getSelectColumn(att));
      } else {
        switch (att.type) {
          case "multiselect":
            columnDefs.push(this.getMultiSelectColumn(att));
            break;
          case "boolean":
            columnDefs.push(this.getBooleanColumn(att));
            break;
          default:
            columnDefs.push(this.getDefaultColumn(att));
            break;
        }
      }
    });

    // Add line actions (up / down / delete) in a single pinned-right column.
    columnDefs.push(this.getActionsColumn())

    return columnDefs;
  }

  // Return the ag-grid object corresponding to our column cell type.
  getSelectColumn(att: Attribute) {
    return {
      headerName: att.label,
      field: att.id,
      cellEditor: "GridImportSelectEditorComponent",
      cellRendererParams: { attribute: att },
      valueFormatter: (params: any) => {
        if (att.values) {
          const index = att.values?.findIndex(
            (val: AttributeValues) => val.code == params.value.value
          );
          if (index != -1) {
            return att.values[index].label;
          } else {
            return params.value.value;
          }
        }
      },
    };
  }

  // Return the ag-grid object corresponding to our column cell type.
  getRowNumberColumn() {
    return {
      headerName: '#',
      minWidth: 30,
      width: 30,
      editable: false,
      pinned: 'left',
      valueGetter: (params) => params.node.rowIndex + 1,
      sortable: false,
      filter: false,
      suppressMenu: true,
    }
  }

  // Return the line actions column (move up / down / delete), pinned on the right.
  getActionsColumn() {
    return {
      headerName: '',
      minWidth: 105,
      width: 105,
      editable: false,
      pinned: 'right',
      lockPosition: true,
      cellRenderer: 'GridLineActions',
      valueGetter: (params) => params.node.rowIndex + 1,
      sortable: false,
      filter: false,
      suppressMenu: true,
    }
  }

  // Return the ag-grid object corresponding to our column cell type.
  getMultiSelectColumn(att: Attribute) {
    return {
      headerName: att.label,
      field: att.id,
      cellEditor: "GridImportMultiSelectEditorComponent",
      cellRendererParams: { attribute: att },
      width: 300,
      valueFormatter: (params: any) => {
        if (att.values) {
          const values: AttributeValues[] = params.value.value.map((code: string) => {
            return att.values.find((val: AttributeValues) => val.code == code);
          });
          if (values.length > 0) {
            return values
              .map((val: AttributeValues) => {
                return val?.label ?? this.$t("import_csv.unknown");
              })
              .join(", ");
          } else {
            return params.value.value;
          }
        }
      },
    };
  }

  // Return the ag-grid object corresponding to our default column type (text).
  getDefaultColumn(att: Attribute) {
    return {
      headerName: att.label,
      field: att.id,
      cellEditor: "GridImportTextEditorComponent",
      cellRendererParams: { type: att.type },
      valueFormatter: (params: any) => {
        // Une date validée (format ISO de l'input date, envoyé tel quel à l'api)
        // est affichée au format de la langue de l'utilisateur.
        if (ImportGrid.isDate(att) && params.value.validity === CellValidity.valid) {
          const date = moment(params.value.value, ImportGrid.ISO_DATE, true);
          if (date.isValid()) {
            return date.format("L");
          }
        }
        return params.value.value;
      },
    };
  }

  // Return the ag-grid object corresponding to our boolean column type.
  getBooleanColumn(att: Attribute) {
    return {
      headerName: att.label,
      field: att.id,
      cellRendererFramework: "GridBoolComponent",
      cellEditor: "GridImportBooleanEditorComponent",
      valueFormatter: (params: any) => {
        return params.value.value;
      },
      cellRendererParams: { isImport: true },
    };
  }

  // The csv parser return an array of data. We transform it as an object in order to send in the ag-grid.
  // We also add an attribute with the validity of the data.
  convertToRowData(data: []): object {
    const result: any = {};

    data.forEach((cell: any, index: number) => {
      const att = this.attributes[index];
      let value = cell;
      // Dans le cas d'un multiselect, on filtre les données par ne conserver que les codes qui existent.
      if (att.type == "multiselect") {
        value = [];
        cell.split(",").forEach((code: string) => {
          if (att.values.findIndex((val: AttributeValues) => val.code == code) != -1) {
            value.push(code);
          }
        });
      }
      result[att.id] = { value: value, validity: CellValidity.unverified };
    });
    return result;
  }

  // Retourne false (après redirection) si le csv ne correspond pas au template de l'api.
  async generateAttributeList(header: []): Promise<boolean> {
    this.attributes = [];

    for (let i = 0; i < header.length; i++) {
      const attribute = header[i];
      // On vérifie que la colonne match avec le template de l'api.
      if (attribute == this.templateValidator[i]) {
        const att = store.getters["attribute/getAttributeByLabel"](this.attributeGroup, attribute);
        if (att) {
          this.attributes.push(new Attribute(att));
        }
      } else {
        await router.push(this.previousRoute);
        this.$eventHub.$emit("show-snackbar", {
          type: MessageType.error,
          mainText: i18n.t("message.csv_matrix_mismatch").toString(),
        });
        // On s'arrête au premier écart, sinon on redirige une fois par colonne invalide.
        return false;
      }
    }
    return true;
  }

  // Return a boolean "The row is valid or not"
  async dataValidation(row: RowNode) {
    let rowIsValid = true; // La ligne est-elle valide ?

    // On utilise une boucle `for ... of` (et non `forEach`) afin de pouvoir
    // `await` les vérifications asynchrones (ex. contrôle d'email en base)
    // avant le `redrawRows` plus bas, sinon la grille était redessinée avant
    // la fin des `await` et la validité de la cellule n'était pas prise en compte.
    const entries = Object.entries(row.data);
    for (let index = 0; index < entries.length; index++) {
      const [key, value] = entries[index];
      let validity = true; // L'attribut est valide.
      let v = (value as ImportGridData).value;
      const att = this.attributes[index];
      // Les dates sont stockées au format ISO : c'est le seul format que l'input
      // date sait afficher, et la valeur envoyée à l'api est ainsi toujours la même,
      // qu'elle vienne du csv (format ISO ou de la langue de l'utilisateur) ou de l'input.
      if (ImportGrid.isDate(att) && v) {
        v = ImportGrid.toIsoDate(v);
        row.data[key].value = v;
      }
      // Check if the cell is required or not.
      const noData = att.type == "multiselect" ? v.length == 0 : v === "";
      if (att.required && noData) {
        rowIsValid = false;
        this.setCellValidity(row, key, CellValidity.required);
        this.setCellTitle(row, key, i18n.t(`import_csv.required`).toString());

        continue;
      } else if (!noData) {
        // Check the attribute type.
        if (att.isSelect) {
          //We use the attribute rules to update the items list to draw.
          const items = this.attributeHelper.allActiveItem(att, formatImportGridDataToData(row.data));
          validity = this.selectValidation(items, row, key, v);
        } else if (att.type == "boolean") {
          validity = GridBoolComponent.isBoolean(v);
        } else if (att.type == "numeric") {
          validity = !isNaN(Number(v));
        } else if (ImportGrid.isDate(att)) {
          validity = moment(v, ImportGrid.ISO_DATE, true).isValid();
        }

        if (!validity) {
          this.setCellTitle(row, key, i18n.t(`import_csv.invalid`).toString());
        }

        // Numéro de SIRET
        if (key.includes("siret")) {
          validity = typeof formValidation.isValidSiret(v) != "string";
          this.setCellTitle(row, key, i18n.t(`import_csv.invalid`).toString());
        }
        // Numéro d'accise
        else if (key.includes("excise")) {
          const countryKey = Object.entries(row.data).find(([key]) =>
            key.includes("country")
          )[0];
          validity = typeof formValidation.isExcise(v, row.data[countryKey].value) != "string";

          this.setCellTitle(row, key, `Invalid ${key}`);
        }
        // Search key named email
        else if (key.includes("email")) {
          const emailKey = Object.entries(row.data).find(([key]) =>
            key.includes("email")
          )[0];
          validity = true;

          // On vérifie d'abord que la valeur est bien un email valide.
          // Un email vide n'est pas une erreur ici (le caractère obligatoire
          // est déjà géré plus haut via `att.required`).
          if (v && typeof formValidation.isEmail(v) === "string") {
            this.setCellTitle(row, key, i18n.t(`import_csv.email_format`).toString());
            validity = false;
          } else if (att.rules?.isUniqueEmail === true) {
            // Format correct : on vérifie l'unicité en base uniquement si la
            // règle `isUniqueEmail` est activée sur l'attribut.
            // Le `redrawRows` en fin de méthode s'exécute désormais après cet
            // `await` grâce à la boucle `for ... of` (cf. en-tête de la méthode).
            const emailReturned = await userApiProvider.getUserEmail(row.data[emailKey].value);
            if (row.data[emailKey].value === emailReturned) {
              this.setCellTitle(row, key, i18n.t(`import_csv.email_already_used`).toString());
              validity = false;
            }
          }
        }

        // Regex validation
        // Le format d'une date est contrôlé ci-dessus, après sa conversion en ISO.
        if (!ImportGrid.isDate(att) && att.regex && att.regex != "" && !v.match(att.regex)) {
          this.setCellTitle(row, key, `Regex : ${att.regex}`);
          validity = false;
        }
      }
      // Set cell state.
      this.setCellValidity(row, key, validity ? CellValidity.valid : CellValidity.invalid);
      if (!validity) rowIsValid = false;
    }

    this.gridOptions.api.redrawRows({ rowNodes: [row] });

    // We update the list of all invalid product.
    if (!rowIsValid && !this.invalidRow.has(row.id)) {
      this.invalidRow.set(row.id, row);
    }
    if (rowIsValid && this.invalidRow.has(row.id)) {
      this.invalidRow.delete(row.id);
    }
  }

  static readonly ISO_DATE = "YYYY-MM-DD";

  // Attribut saisi via l'input date (cf. GridImportTextEditorComponent).
  static isDate(att: Attribute): boolean {
    return att.type == "date" || att.type == "datetime";
  }

  // Convertit une date au format ISO ou au format de la langue de l'utilisateur
  // (ex. 06/10/2026 en FR) au format ISO. Une valeur non reconnue est conservée
  // telle quelle afin d'être signalée invalide.
  static toIsoDate(value: string): string {
    const date = moment(value, [ImportGrid.ISO_DATE, moment.localeData().longDateFormat("L")], true);
    return date.isValid() ? date.format(ImportGrid.ISO_DATE) : value;
  }

  // This method validate or not a selected cell.
  selectValidation(att: AttributeValues[], node: RowNode, key: string, value: any): boolean {
    const valid = att.findIndex((val: AttributeValues) => val.code == value) != -1;
    if (valid) {
      this.setCellValidity(node, key, CellValidity.valid);
    } else {
      this.setCellValidity(node, key, CellValidity.invalid);
    }
    return valid;
  }

  setCellValidity(row: RowNode, key: string, validity: CellValidity) {
    row.data[key].validity = validity;
  }

  setCellTitle(row: RowNode, key: string, title: string) {
    row.data[key].title = title;
  }

  public deleteRowNode(rowData?: any, rowIndex?: number): void {
    const rowNode = this.gridOptions.api.getDisplayedRowAtIndex(rowIndex);

    if (rowNode) {
      // On retire la ligne du suivi des lignes invalides, sinon la validation
      // continue de la comptabiliser alors qu'elle n'existe plus dans la grille.
      if (this.invalidRow.has(rowNode.id)) {
        this.invalidRow.delete(rowNode.id);
      }

      // On supprime via l'API ag-grid (source de vérité). Inutile de toucher
      // `this.rowData` : il est réassigné après le parse et `componentParent`
      // pointe sur l'instance de collecte des données (tableau vide ici).
      this.gridOptions.api.applyTransaction({ remove: [rowNode.data] });
    }
  }

  // Move a line up (direction = -1) or down (direction = 1) in the grid.
  // On s'appuie uniquement sur l'API ag-grid (source de vérité) : `this.rowData`
  // n'est pas fiable ici car il est réassigné après le parse, alors que
  // `context.componentParent` pointe sur l'instance de collecte des données.
  public moveRowNode(rowIndex: number, direction: number): void {
    const rowCount = this.gridOptions.api.getDisplayedRowCount();
    const newIndex = rowIndex + direction;

    // Ne rien faire si on est déjà en haut / en bas de la grille.
    if (newIndex < 0 || newIndex >= rowCount) {
      return;
    }

    const rowNode = this.gridOptions.api.getDisplayedRowAtIndex(rowIndex);
    if (!rowNode) {
      return;
    }
    const data = rowNode.data;

    // On applique le nouvel ordre à ag-grid via une transaction, comme la
    // suppression. Un `setRowData` impératif n'est pas pris en compte tant que
    // la grille est pilotée par la prop `:rowData`.
    this.gridOptions.api.applyTransaction({ remove: [data] });
    this.gridOptions.api.applyTransaction({ add: [data], addIndex: newIndex });

    // Rafraîchit les cellules pour recalculer les numéros de ligne (#) et
    // l'état activé/désactivé des flèches haut/bas.
    this.gridOptions.api.refreshCells({ force: true });
  }

  // Add a blank row at the end of the grid, using the same cell format as an imported line.
  public async addEmptyRow(): Promise<void> {
    if (!this.attributes || this.attributes.length === 0) {
      return;
    }

    const newRow: any = {};
    this.attributes.forEach((att: Attribute) => {
      newRow[att.id] = {
        value: att.type === "multiselect" ? [] : "",
        validity: CellValidity.unverified,
      };
    });

    const result = this.gridOptions.api.applyTransaction({ add: [newRow] });
    this.rowData.push(newRow as never);

    // Validate the new node so required/invalid cells are highlighted immediately.
    if (result && result.add && result.add.length) {
      await this.dataValidation(result.add[0]);
    }
    this.$forceUpdate();
  }
}
